import { el } from '@/ui/dom';
import { sfxUiTick } from '@/audio/sfx';
import { BUY_GAME_LABEL, CROWN_SVG, CHEV_SVG } from '@/ui/lockCta';

/**
 * promoPanel.ts — M139 (owner: "the buy module is too small on the garage —
 * it needs to be the main visual grab for a logged-out user"). The big sell
 * a non-owning web visitor sees on the Garage, the World Tour Cup city
 * showcase (M147), the Shop and Customize: a headline, the one-purchase
 * promise, a large BUY THE GAME button and the small LOG IN link.
 *
 * M147 (owner): the live "NOW SHOWING" line is gone, and the freed space
 * goes to the promise — ONE-TIME PURCHASE · NO SUBSCRIPTION · FREE UPDATES
 * as three gold-checked items right above the button, instead of the small
 * grey note that used to sit under it.
 *
 * Never rendered on iOS (the paid app has nothing to sell — Flow only builds
 * a promo when the web paywall is armed).
 */

export interface PromoPanelOptions {
  kicker: string;
  headline: string;
  /** One line of what the game holds, e.g. "25 KITS · 64 FLAGS · 13 MODS". */
  sub?: string;
  price: string;
  onBuy: () => void;
  onLogIn?: () => void;
  className?: string;
}

export interface PromoPanel {
  root: HTMLDivElement;
}

/** The one-purchase promise, in the order the owner lists it. No em dashes. */
export const PROMO_PROMISE: readonly string[] = [
  'ONE-TIME PURCHASE',
  'NO SUBSCRIPTION',
  'FREE UPDATES',
];

const CHECK_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5.5 12.5 4.2 4.2 8.8-9.4"/></svg>';

export function promoPanel(parent: HTMLElement, opts: PromoPanelOptions): PromoPanel {
  const root = el('div', `promo-panel${opts.className ? ` ${opts.className}` : ''}`, parent);
  el('div', 'promo-kicker', root).textContent = opts.kicker;
  el('div', 'promo-headline', root).textContent = opts.headline;
  if (opts.sub) el('div', 'promo-sub', root).textContent = opts.sub;

  const promise = el('div', 'promo-promise', root);
  for (const line of PROMO_PROMISE) {
    const item = el('span', 'promo-promise-item', promise);
    el('span', 'promo-promise-ic', item).innerHTML = CHECK_SVG;
    el('span', 'promo-promise-label', item).textContent = line;
  }

  // The HOME SCREEN's buy pill, verbatim (owner: the popup matches the new
  // home style) — crown slab · BUY THE GAME · price · chevron — just bigger.
  const buy = el('button', 'tp-buy promo-buy', root);
  buy.type = 'button';
  buy.setAttribute('aria-label', `${BUY_GAME_LABEL}, ${opts.price}`);
  el('span', 'tpb-ic', buy).innerHTML = CROWN_SVG;
  el('span', 'tpb-label', buy).textContent = BUY_GAME_LABEL;
  el('span', 'tpb-price', buy).textContent = opts.price;
  el('span', 'tpb-chev', buy).innerHTML = CHEV_SVG;
  el('span', 'tpb-sheen', buy);
  buy.addEventListener('click', () => {
    sfxUiTick();
    opts.onBuy();
  });

  if (opts.onLogIn) {
    const onLogIn = opts.onLogIn;
    const link = el('button', 'lock-cta-login', root);
    link.type = 'button';
    el('span', 'lock-cta-login-q', link).textContent = 'ALREADY OWN IT?';
    el('span', 'lock-cta-login-a', link).textContent = 'LOG IN ›';
    link.addEventListener('click', () => {
      sfxUiTick();
      onLogIn();
    });
  }

  return { root };
}
